import { useEffect, useState } from 'react';
import { clockGeneration, serverNow } from './showClock';

export const nextBoundary = (boundaries: readonly number[], now: number): number | null =>
  boundaries.reduce<number | null>((next, at) => (at > now && (next === null || at < next) ? at : next), null);

export interface ServerClock {
  now: number;
  resumes: number;
}

export const useServerClockAt = (boundaries: readonly number[]): ServerClock => {
  const [clock, setClock] = useState<ServerClock>(() => ({ now: serverNow(), resumes: 0 }));
  const generation = clockGeneration();
  const list = boundaries.join(',');
  const key = `${list}|${generation}`;

  useEffect(() => {
    setClock((prev) => ({ ...prev, now: serverNow() }));
  }, [key]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setClock((prev) => ({ now: serverNow(), resumes: prev.resumes + 1 }));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const next = nextBoundary(list ? list.split(',').map(Number) : [], serverNow());
    if (next === null) return;
    const timer = window.setTimeout(
      () => setClock((prev) => ({ ...prev, now: serverNow() })),
      Math.max(0, next - serverNow()) + 20,
    );
    return () => window.clearTimeout(timer);
  }, [list, key, clock.now]);

  return clock;
};
